import React from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui';

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  /** Body content — plain text or rich nodes. */
  children: React.ReactNode;
  confirmLabel: string;
  confirmVariant?: 'primary' | 'danger';
  confirmDisabled?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Small confirm dialog used across the Scheduler page (run-a-step-now,
 * delete-a-post). Reuses the global `.modal-*` shell so it stays visually
 * consistent with the other scheduler modals.
 */
function ConfirmModal({
  isOpen,
  title,
  children,
  confirmLabel,
  confirmVariant = 'primary',
  confirmDisabled = false,
  loading = false,
  onConfirm,
  onClose,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  return createPortal(
    <div className="modal-overlay" onClick={loading ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close dialog"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            loading={loading}
            disabled={loading || confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmModal;
